import axios from "axios";
import LocalStorageService from "./LocalStorageService";

const localStorageService = LocalStorageService.getService();

export const baseUrl = process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api/` : '/api/';

/********************* Axios Configuration *********************/
/**
 * Axios base IF authenticated
 * @type {AxiosInstance}
 */
const API = axios.create({
    baseURL: baseUrl,
    timeout: 30 * 1000,
    headers: {
        'Content-Type': 'application/json',
    }
});


/********************** Request Interceptors *******************/
/**
 * Below intercepts API requests and passes in authorization headers
 */
API.interceptors.request.use(
    config => {
        const token = localStorageService.getAccessToken();
        if (token) {
            config.headers['Authorization'] = 'token ' + token;
        }
        return config;
    },
    error => {
        Promise.reject(error)
    });

/******************** Response Interceptor ********************/
/**
 * Below intercepts API responses and takes action based on status code
 * If request client gets a 401 response, we clear the API token and reroute to the root app, forcing login. Unless
 * the request stems from the login page, if so ignore the redirect.
 */
API.interceptors.response.use((response) => {
    return response
}, function (error) {
    if (error.response.status === 401) {
        // Clear API token and send user to root app
        localStorageService.clearToken();

        if (!window.location.pathname.startsWith('/login')) {
            window.location = '/login';
        }

        return Promise.reject(error);
    }
    return Promise.reject(error);
});

/********************* Projects *********************/
/**
 * Gets all projects from database that user/org has access to
 */
export const getProjects = async (params) => {
    const response = await API.get(`/project${params}`);
    return response.data
};

export const getProject = async (id) => {
    const response = await API.get(`/project/${id}`)
    return response.data
}

/**
 * Creates a new project in database
 */
export const createProject = async (data) => {
    return await API.post('/project', data)
}

/**
 * Deletes a project by id
 */
export const deleteProject = async (id) => {
    return await API.delete(`/project/${id}`)
}

/********************* Reports *********************/
/**
 * Gets all reports from database that user/org has access to
 */
export const getReports = async (projectId = null) => {
    const response = await API.get(`/reports/${projectId}`);
    return response.data;
};

/**
 * TODO
 */
export const getAllReports = async (params) => {
    const response = await API.get(`/report${params}`);
    return response.data;
};

/**
 * Gets report by id
 */
export const getReport = async (reportId) => {
    const response = await API.get(`/report/${reportId}`);
    return response.data;
}

/**
 * Creates a new report in database
 */
export const createReport = async (data) => {
    return await API.post('/report', data);
}

/**
 * Deletes a report by id
 */
export const deleteReport = async (id) => {
    return await API.delete(`/report/${id}`);
}

export const getReportDocuments = async (key) => {
    return await API.get(`/reports/documents/${key}`);
}

/********************* Documents *********************/
/**
 * Gets all documents from database that user/org has access to
 */
export const getDocuments = async (report_id) => {
    const response = await API.get(`/document/${report_id}`);
    return response.data
};

/**
 * Creates a new document in database
 */
export const createDocument = async (data) => {
    return await API.post('/document', data)
}

/**
 * Deletes a document by id
 */
export const deleteDocument = async (id) => {
    return await API.delete(`/document/${id}`)
}

/********************* Organizations *********************/
/**
 * Gets all organizations from database (must be superuser)
 */
export const getOrganizations = async (params) => {
    const response = await API.get(`/organization${params}`);
    return response.data;
}

/**
 * Get organization by id
 */
export const getOrganization = async (id) => {
    const response = await API.get(`/organization/${id}`);
    return response.data;
}

/**
 * Creates a new organization in database (must be superuser)
 */
export const createOrganization = async (data) => {
    return await API.post('/organization', data)
}

/**
 * Get current organization based on requesting user
 */
export const getCurrentOrganization = async () => {
    return await API.get('/organization/current')
}

/********************* Users *********************/

/**
 * Gets organization by logged in user
 */
export const getCurrentUser = async () => {
    const response = await API.get('/current-user');
    return response.data;
}

/**
 * Gets all users (if VK org, show all, otherwise show org users)
 */
export const getUsers = async (params) => {
    const response = await API.get(`/user${params}`);
    return response.data;
}

/**
 * Get user by id
 */
export const getUser = async (id) => {
    const response = await API.get(`/user/${id}`)
    return response.data;
}


/**
 * Creates a new user (must be superuser)
 */
export const createUser = async (data) => {
    return await API.post('/user', data)
}

/**
 * Deletes user by id
 * @param id
 * @returns {Promise<*>}
 */
export const deleteUser = async (id) => {
    return await API.delete(`/user/${id}`)
}

/**
 * Updates user
 * @param id
 * @param data
 * @returns {Promise<*>}
 */
export const updateUser = async (id, data) => {
    const response = await API.patch(`/user/${id}`, data);
    return response.data;
}

/**
 * Get themes
 */
export const getThemes = async () => {
    const response = await API.get('/theme')
    return response.data
}

export const updateTheme = async (data) => {
    const response = await API.put(`/theme/${data.id}`, data);
    return response.data
}

/**
 * Get Current Theme
 */
export const getCurrentTheme = async () => {
    return await API.get(`/theme/current`);
}

/********************* Reports Stats *********************/
/**
 * Get Report Status Counts
 */
export const getReportStats = async () => {
    const response = await API.get(`/reports/stats`)
    return response.data
}

export const changePassword = async (params) => {
    return API.put(`/change-password/`, params);
}

// Demo endpoint - kicks off notification from backend
export const getNotifications = async () => {
    const respone = await API.get(`/notifications/`);
    return respone;
}

// Password Reset
export const requestPasswordReset = async (email) => {
    const response = await API.post(`/password-reset/`, email);
    return response;
}

export const resetPassword = async (params) => {
    const response = await API.post(`/password-reset/confirm/`, params);
    return response;
}

export const registerUser = async (params) => {
    return API.post(`/register/`, params);
}


/********************* Region Choices API *********************/
// Get regions as list of values
export const getAllRegionsOptions = async () => {
    const response = await API.get(`/refinitiv/region_choice/`);
    return response.data;
}

/********************* Country Choices API *********************/
// Get countries as list of values
export const getAllCountryOptions = async () => {
    const response = await API.get(`/refinitiv/country_choice/`);
    return response.data;
}

/********************* Deliveries API *********************/
export const getAllDeliveries = async (params) => {
    const response = await API.get(`/refinitiv/deliveries${params}`);
    return response.data;
}

export const getRecentDeliveries = async () => {
    const response = await API.get(`/refinitiv/deliveries/?recent=true`);
    return response.data;
}

export const getFavoriteDeliveries = async (params) => {
    const response = await API.get(`/refinitiv/deliveries/${params}&favorites=true`);
    return response.data;
}


export const addDeliveryFavorite = async (name) => {
    const response = await API.patch(`/refinitiv/deliveries/${name}/favorite/`, null);
    return response.data;
}

export const removeDeliveryFavorite = async (name) => {
    const response = await API.delete(`/refinitiv/deliveries/${name}/favorite/`, null);
    return response.data;
}


/********************* Collection Statuses API *********************/
export const getCollectionStatuses = async (params) => {
    const response = await API.get(`/refinitiv/status${params}`);
    return response.data;
}

export const getFavoriteCollectionStatuses = async (params) => {
    const response = await API.get(`/refinitiv/status${params}&favorites=true`);
    return response.data;
}

export const addCollectionStatusFavorite = async (name) => {
    const response = await API.patch(`/refinitiv/status/${name}/favorite/`, null);
    return response.data;
}

export const removeCollectionStatusFavorite = async (name) => {
    const response = await API.delete(`/refinitiv/status/${name}/favorite/`, null);
    return response.data;
}

/********************* Dashboard API *********************/
export const getRefinitivDashboardStats = async (params) => {
    const response = await API.get(`/refinitiv/stats`);
    return response.data;
}

/********************* Metadata API *********************/
export const createMetadata = async (payload) => {
    const response = await API.post(`/refinitiv/metadata/`, payload);
    return response.data;
}

export const updateMetadata = async (id, payload) => {
    const response = await API.patch(`/refinitiv/metadata/${id}/`, payload);
    return response.data;
}